// components/form/fields/VariantBuilder.tsx
import React from "react";
import { useFieldArray, useFormContext, Controller } from "react-hook-form";
import { ImageUploader } from "../../components/ImageUploader";

interface VariantBuilderProps {
  field: any; // from FieldRenderer
}

export const VariantBuilder: React.FC<VariantBuilderProps> = ({ field }) => {
  const { control, register, setValue, watch } = useFormContext();

  const variants = watch("variants") || [];

  const { fields, append, remove } = useFieldArray({
    control,
    name: "variants",
  });

  return (
    <div className="space-y-4">
      {fields.map((variant, index) => (
        <div
          key={variant.id}
          className="p-4 border border-gray-300 dark:border-gray-600 rounded-xl space-y-3 bg-gray-50 dark:bg-gray-700"
        >
          <div className="flex justify-between items-center">
            <h3 className="font-semibold text-gray-800 dark:text-gray-200">
              Variant {index + 1}
            </h3>
            <button
              type="button"
              onClick={() => remove(index)}
              className="text-red-600 dark:text-red-400 hover:underline text-sm"
            >
              Remove
            </button>
          </div>

          {/* SKU */}
          <div>
            <label className="block text-sm font-medium mb-1">SKU</label>
            <input
              {...register(`variants.${index}.sku`, { required: true })}
              className="w-full border p-2 rounded dark:bg-gray-600 dark:text-gray-100"
            />
          </div>

          {/* Variant Name */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Variant Name
            </label>
            <input
              {...register(`variants.${index}.variant_name`, {
                required: true,
              })}
              className="w-full border p-2 rounded dark:bg-gray-600 dark:text-gray-100"
            />
          </div>

          {/* Base Price */}
          <div>
            <label className="block text-sm font-medium mb-1">Price</label>
            <input
              type="number"
              {...register(`variants.${index}.base_price`, { required: true })}
              className="w-full border p-2 rounded dark:bg-gray-600 dark:text-gray-100"
            />
          </div>

          {/* Stock Quantity */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Stock Quantity
            </label>
            <input
              type="number"
              {...register(`variants.${index}.stock_quantity`, {
                required: true,
              })}
              className="w-full border p-2 rounded dark:bg-gray-600 dark:text-gray-100"
            />
          </div>

          {/* Images */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Variant Images
            </label>
            <Controller
              control={control}
              name={`variants.${index}.variant_images`}
              render={({ field: controllerField }) => (
                <ImageUploader
                  initialImages={controllerField.value || []}
                  onUploadComplete={(urls: string[]) => {
                    controllerField.onChange(urls);
                  }}
                />
              )}
            />
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={() =>
          append({
            sku: "",
            variant_name: "",
            base_price: 0,
            stock_quantity: 0,
            variant_images: [],
          })
        }
        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
      >
        Add Variant
      </button>
    </div>
  );
};